import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationProfile } from '@/hooks/useOrganizationProfile';

// Default Lumenous branding
const DEFAULT_BRANDING = {
  logoUrl: '',
  primaryColor: '',
  accentColor: '',
  companyName: 'Lumenous',
  faviconUrl: '',
  isWhiteLabeled: false,
};

interface WhiteLabelSettings {
  logoUrl: string;
  primaryColor: string;
  accentColor: string;
  companyName: string;
  faviconUrl: string;
  isWhiteLabeled: boolean;
}

interface WhiteLabelContextType extends WhiteLabelSettings {
  loading: boolean;
}

const WhiteLabelContext = createContext<WhiteLabelContextType | undefined>(undefined);

export function WhiteLabelProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useOrganizationProfile();
  const [settings, setSettings] = useState<WhiteLabelSettings>(DEFAULT_BRANDING);

  // Update settings when org profile changes
  useEffect(() => {
    if (!user || !profile) {
      setSettings(DEFAULT_BRANDING);
      return;
    }

    if (profile.white_label_enabled) {
      setSettings({
        logoUrl: profile.custom_logo_url || '',
        primaryColor: profile.primary_color || '',
        accentColor: profile.accent_color || '',
        companyName: profile.company_name || 'Lumenous',
        faviconUrl: profile.favicon_url || '',
        isWhiteLabeled: true,
      });
    } else {
      setSettings(DEFAULT_BRANDING);
    }
  }, [user, profile]);

  // Apply CSS custom properties when white-label is enabled
  useEffect(() => {
    const root = document.documentElement;

    if (settings.isWhiteLabeled) {
      if (settings.primaryColor) {
        root.style.setProperty('--wl-primary-color', settings.primaryColor);
        // Convert hex to HSL-ish values for Tailwind/shadcn compatibility
        const rgb = hexToRgb(settings.primaryColor);
        if (rgb) {
          root.style.setProperty('--wl-primary-r', String(rgb.r));
          root.style.setProperty('--wl-primary-g', String(rgb.g));
          root.style.setProperty('--wl-primary-b', String(rgb.b));
        }
      }
      if (settings.accentColor) {
        root.style.setProperty('--wl-accent-color', settings.accentColor);
      }
    } else {
      // Remove custom properties when white-label is off
      root.style.removeProperty('--wl-primary-color');
      root.style.removeProperty('--wl-primary-r');
      root.style.removeProperty('--wl-primary-g');
      root.style.removeProperty('--wl-primary-b');
      root.style.removeProperty('--wl-accent-color');
    }

    return () => {
      root.style.removeProperty('--wl-primary-color');
      root.style.removeProperty('--wl-primary-r');
      root.style.removeProperty('--wl-primary-g');
      root.style.removeProperty('--wl-primary-b');
      root.style.removeProperty('--wl-accent-color');
    };
  }, [settings]);

  // Update favicon when white-label is enabled
  useEffect(() => {
    if (settings.isWhiteLabeled && settings.faviconUrl) {
      const existingLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (existingLink) {
        existingLink.href = settings.faviconUrl;
      } else {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = settings.faviconUrl;
        document.head.appendChild(link);
      }
    }
  }, [settings.isWhiteLabeled, settings.faviconUrl]);

  const value: WhiteLabelContextType = {
    ...settings,
    loading: profileLoading,
  };

  return (
    <WhiteLabelContext.Provider value={value}>
      {children}
    </WhiteLabelContext.Provider>
  );
}

export function useWhiteLabel() {
  const context = useContext(WhiteLabelContext);
  if (context === undefined) {
    throw new Error('useWhiteLabel must be used within a WhiteLabelProvider');
  }
  return context;
}

// Utility: convert hex color to RGB
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}
